import { Box, IconButton } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { TASK_ID_QUERY_KEY } from "@src/components/widgets/Modals/ModalCommunication/CommunicationModalHeader";
import {
  useHasCommentIdInUri,
  useHasTaskIdInUri,
} from "@src/components/widgets/Modals/ModalCommunication/ModalCommunication";
import { TaskTabIdEnum } from "@src/constants/tasks";
import { trackEvent } from "@src/services/amplitude";
import { headTitle } from "@src/utils/head-title";
import { useDeployAvailableNotification, useStore } from "@src/utils/hooks";
import { getSvgSpriteName } from "@src/utils/svg-sprite-name";
import { Dialogs } from "@src/widgets/Dialogs";
import { debounce } from "lodash";
import { observer } from "mobx-react-lite";
import Head from "next/head";
import Router from "next/router";
import React, { type FC, type ReactNode, useEffect, useState } from "react";
import { AppNav } from "../AppNav";
import { ContentContainer } from "../ContentContainer";
import type { ContentContainerProps } from "../ContentContainer/ContentContainer";
import { TrialNav } from "../TrialNav";
import { UnpaidBanner } from "../unpaid-banner/unpaid-banner";
import { useGetRunningTimer } from "./useGetRunningTimer";

type MainLayoutProps = {
  title?: string;
  navigation?: ReactNode;
  hideAppNav?: boolean;
  fixedNavigation?: boolean;
  withNavigationBorder?: boolean;
  noTopMargin?: boolean;
  noBottomMargin?: boolean;
  scrollToTopButton?: boolean;
} & ContentContainerProps;

const MainLayout: FC<React.PropsWithChildren<MainLayoutProps>> = ({
  title,
  navigation,
  hideAppNav,
  noTopMargin,
  noBottomMargin,
  fixedNavigation,
  withNavigationBorder,
  scrollToTopButton,
  children,
  ...contentContainerProps
}) => {
  const { taskDetailModalStore } = useStore();
  const [scrollButtonVisible, setScrollButtonVisible] = useState(false);
  useGetRunningTimer();
  useDeployAvailableNotification();

  useHasTaskIdInUri((id) => {
    if (Router.query[TASK_ID_QUERY_KEY] !== id) return;

    const { disclosureState } = taskDetailModalStore;

    if (disclosureState.isOpen && disclosureState.additionalData?.id === id) {
      return;
    }

    disclosureState.onOpen({ id });
  });
  useHasCommentIdInUri(() => {
    taskDetailModalStore.activeTabId.set(TaskTabIdEnum.Communication);
  });

  useEffect(() => {
    if (!title) return;
    trackEvent("page-load", title);
  }, []);

  const handleScroll = () => {
    if (window.scrollY < 150) {
      setScrollButtonVisible(false);
      return;
    }

    setScrollButtonVisible(true);
  };

  const debouncedHandleScroll = debounce(handleScroll, 100);

  useEffect(() => {
    if (!scrollToTopButton) {
      return () => {
        window.removeEventListener("scroll", debouncedHandleScroll);
      };
    }

    window.addEventListener("scroll", debouncedHandleScroll);

    return () => {
      window.removeEventListener("scroll", debouncedHandleScroll);
    };
  }, [debouncedHandleScroll, scrollToTopButton]);

  return (
    <React.Fragment>
      <Head>
        <title>{headTitle(title)}</title>
        <meta property="og:title" content={headTitle(title)} key="title" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
          key="viewport"
        />
        <link
          rel="icon"
          type="image/x-icon"
          sizes="48x48"
          href="/favicon.ico"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="192x192"
          href="/android-chrome-192x192.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="512x512"
          href="/android-chrome-512x512.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <link
          rel="preload"
          as="image"
          type="image/svg+xml"
          href={`/${getSvgSpriteName()}`}
        />
      </Head>
      {/* TODO: hardcoded color, use new grey from palette */}
      <Box as="main" minH="100vh" bg="#F0F4F8">
        <TrialNav />
        {hideAppNav || <AppNav />}
        <Box
          pt={{
            base: "0",
            lg: fixedNavigation ? "52px" : 0,
          }}
          pb={{ base: 20, lg: 0 }}
          pl={{ lg: 20 }}
        >
          <Box pos="sticky" zIndex="100" top="0" right="0" left={{ lg: 20 }}>
            <UnpaidBanner />
            {!!navigation && (
              <Box
                bg="white"
                borderColor={withNavigationBorder ? "gray.100" : "none"}
                borderBottomWidth={withNavigationBorder ? "1px" : "none"}
              >
                <ContentContainer>{navigation}</ContentContainer>
              </Box>
            )}
          </Box>
          <Box
            flex="1"
            pt={{
              base: noTopMargin ? 0 : "6",
              lg: noTopMargin ? 0 : "10",
            }}
            pb={{
              base: noBottomMargin ? 0 : "6",
              lg: noBottomMargin ? 0 : "10",
            }}
          >
            <ContentContainer {...contentContainerProps}>
              {children}
            </ContentContainer>
          </Box>
        </Box>
        <Dialogs />

        {scrollToTopButton && (
          <IconButton
            pos="fixed"
            zIndex="docked"
            right={scrollButtonVisible ? "6" : "-10"}
            bottom="24"
            boxSize="10"
            opacity={scrollButtonVisible ? 1 : 0}
            transition="all 150ms"
            aria-label={t`Scroll to top`}
            icon={<Icon name="chevron-up" />}
            onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: "smooth",
              });
            }}
            rounded="full"
          />
        )}
      </Box>
    </React.Fragment>
  );
};

export default observer(MainLayout);
